import {Gallery} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/gallery.types';
import {ImagesSection_VideoButton} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {useHydrationDone} from '@aboutyou/lazy-imports';
import {
  ImageLabelOverlay,
  ImageRatio,
  ProductImage,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {ComponentProps, ComponentType} from 'react';
import {VideoSlide} from './Components/VideoSlide';

type SliderContentProps = {
  activeIndex: number;
  columns: number;
  gallery: Gallery;
  hideSTL?: boolean;
  imageComponent?: ComponentType<ComponentProps<typeof ProductImage>>;
  isLoadingState?: boolean;
  isOpen: boolean;
  onSlideClick?: (index: number) => void;
  ratio?: ImageRatio;
  scrollToShopTheLook?: VoidFunction;
  videoButton?: ImagesSection_VideoButton;
};

export const SliderContent = ({
  activeIndex,
  columns,
  gallery,
  hideSTL = false,
  imageComponent: ProductImageComponent = ProductImage,
  isLoadingState,
  isOpen,
  onSlideClick,
  ratio,
  scrollToShopTheLook,
  ref,
}: SliderContentProps & {ref?: React.Ref<HTMLLIElement>}) => {
  const isInitialMount = !useHydrationDone();

  return (
    <>
      {gallery.elements?.map((item, index) => {
        // On the initial mount we only want to prioritize the first image, as it's the highest priority on the server. In the client we have enough time to prioritize the first two images to preload what the user will most probably unfold soon.
        const isCloseToViewport = isInitialMount
          ? index === activeIndex
          : activeIndex - 1 <= index && activeIndex + 1 >= index;

        const containerProps: ComponentProps<typeof SlideContainer> = {
          isOpen,
          ref,
        };

        const sharedImageSlideProps: Pick<
          ComponentProps<typeof ProductImageComponent>,
          'columns' | 'loading' | 'importance' | 'ratio'
        > = {
          columns: isLoadingState ? undefined : columns, // Allow to use lower resolution images while loading.
          loading: isCloseToViewport ? 'eager' : 'lazy',
          importance: isCloseToViewport ? 'high' : 'low',
          ratio,
        };

        switch (item.type?.$case) {
          case 'image': {
            const image = item.type.image;

            return (
              <SlideContainer
                key={`horizontal-simple-slider-image-${index}`}
                {...containerProps}
                onClick={() => onSlideClick?.(index)}
              >
                <ProductImageComponent
                  {...sharedImageSlideProps}
                  padded={image.image?.isEnclosed}
                  src={image.image.src}
                  alt={image.image.description}
                />
              </SlideContainer>
            );
          }
          case 'shopTheLook': {
            const image = item.type.shopTheLook;
            return (
              <SlideContainer
                key={`horizontal-simple-slider-shop-the-look-${index}`}
                {...containerProps}
                onClick={() => onSlideClick?.(index)}
              >
                <ProductImageComponent
                  {...sharedImageSlideProps}
                  padded={image.image?.isEnclosed}
                  src={image.image.src}
                  alt={image.image.description}
                />

                {image.label && !hideSTL && (
                  <ImageLabelOverlay
                    onClick={() => scrollToShopTheLook?.()}
                    label={image.label}
                  />
                )}
              </SlideContainer>
            );
          }
          case 'video':
            return (
              <SlideContainer
                key={`horizontal-simple-slider-video-${index}`}
                {...containerProps}
                onClick={() => onSlideClick?.(index)}
              >
                <VideoSlide
                  video={item.type.video.video}
                  isFullModeEnabled={isOpen}
                  shouldPreload={isCloseToViewport}
                />
              </SlideContainer>
            );

          default:
            return null;
        }
      })}
    </>
  );
};

export const SlideContainer = styled.li<{
  isOpen: boolean;
}>`
  > div {
    height: ${({isOpen}) => (isOpen ? '100%' : 'auto')};
  }
`;
